import { useEffect, useState } from 'react';

// Types
import type { AxiosError } from 'axios';
import type { SWRInfiniteConfiguration } from 'swr/infinite';
import useSWRInfinite from 'swr/infinite';

import type { SpGroupType } from '@/hooks/swr/group/useSPGroups';

// Utils
import { get } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

import type { MediaType } from '@/types/media/mediaType';

// API Endpoints
import { GROUP_MEDIAS } from '@/lib/routes/apiEndpoints';

type UseGroupMediasProps = {
    groupId: SpGroupType['id'];
    limit: number;
    params?: UseGroupMediasParamsType;
    options: SWRInfiniteConfiguration<GroupMediasPageType, AxiosError<Error>>;
};

type UseGroupMediasParamsType = {
    end: string;
    start: string;
    sort?: {
        column: string;
        order: 'asc' | 'desc';
    };
    filter_post_type?: string[];
    with_albums?: boolean;
    with_labels?: boolean;
};

type GroupMediasPageType = {
    current_page: number;
    first_page_url: string;
    from: number;
    medias: MediaType[];
    next_page_url: string | null;
    path: string;
    per_page: number;
    prev_page_url: string | null;
    to: number;
};

type getKeyType = (
    pageIndex: number,
    previousPageData: GroupMediasPageType | null,
    activeGroupId: UseGroupMediasProps['groupId'],
    limit: UseGroupMediasProps['limit'],
    requestParams: UseGroupMediasProps['params']
) => [string, string] | null;

const getKey: getKeyType = (pageIndex, previousPageData, activeGroupId, limit, requestParams) => {
    // No group ID
    if (!activeGroupId) return null;

    // No request params
    if (!requestParams) return null;

    // No dates
    if (!requestParams?.start || !requestParams?.end) return null;

    // Reached the end
    if (previousPageData && !previousPageData.medias) return null;

    const url = `${injectEndpointUrlParams(GROUP_MEDIAS, {
        GROUP_ID: activeGroupId
    })}?per_page=${limit}`;

    return [
        pageIndex === 0 ? url : `${url}&page=${previousPageData.current_page + 1}`,
        JSON.stringify(requestParams)
    ];
};

const useGroupMedias = ({ groupId, limit = 20, params, options }: UseGroupMediasProps) => {
    const [requestParams, setRequestParams] = useState(params);
    const [activeGroupId, setActiveGroupId] = useState(groupId);

    useEffect(() => {
        setActiveGroupId(groupId);
    }, [groupId]);

    useEffect(() => {
        if (params) {
            setRequestParams(params);
        }
    }, [params]);

    const {
        data: dataMedias,
        size,
        setSize,
        mutate: mutateMedias,
        error: mediasError
    } = useSWRInfinite<GroupMediasPageType, AxiosError<Error>>(
        (pageIndex, previousPageData) =>
            getKey(pageIndex, previousPageData, activeGroupId, limit, requestParams),
        ([url]) => get(url, requestParams).then((res) => res.data),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            revalidateFirstPage: false,
            ...options
        }
    );

    const isLoadingInitialData = !dataMedias && !mediasError;
    const isLoadingMore = size > 0 && dataMedias && typeof dataMedias[size - 1] === 'undefined';

    return {
        dataMedias,
        loadMoreMedia: () => setSize(size + 1), // Function to load more media (load more button)
        mutateMedias,
        mediasError,
        requestParams,
        setRequestParams, // Setter to change parameters in the url
        setActiveGroupId,
        isLoadingInitialData, // Check if it's the initial loading (not the load more)
        isLoadMore:
            dataMedias && typeof dataMedias[dataMedias.length - 1].next_page_url === 'string',
        isLoadingMore // Check if the load more is loading
    };
};

export default useGroupMedias;
